<template>
  <div
    class="fullCalendarSingleAvailability"
    :class="`${$mq}Mode`"
  >
    <FullCalendar
      ref="fullAvailability"
      default-view="timeGridWeek"
      :header="header"
      :plugins="calendarPlugins"
      :events="allAvailabilities"
      :event-constraint="eventConstraint"
      :event-render="eventRender"
      :event-overlap="false"
      :selectable="true"
      :select-min-distance="20"
      :select-overlap="false"
      :select-constraint="selectConstraint"
      :first-day="1"
      :all-day-slot="false"
      :event-time-format="eventTimeFormat"
      :slot-label-format="slotLabelFormat"
      :scroll-time="'08:30:00'"
      :dates-render="updatePeriod"
      :event-allow="eventAllow"
      :now-indicator="true"
      @dateClick="handleDateClick"
      @eventDrop="rescheduleEvent"
      @eventResize="rescheduleEvent"
      @eventClick="removeEvent"
      @select="handleSelect"
    />
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';

export default {
  components: {
    FullCalendar,
  },

  props: {
    recurringAvailabilities: {
      type: Array,
      default() {
        return [];
      },
    },

    exceptionAvailabilities: {
      type: Array,
      default() {
        return [];
      },
    },

    isLoadingExceptions: {
      type: Boolean,
      default: false,
    },

    lastId: {
      type: Number,
      default: 0,
    },

    singleAvailabilityPeriod: {
      type: Object,
      default: null,
    },

    canSaveExceptions: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      header: {
        left: 'today',
        center: 'title',
        right: 'prev,next',
      },
      calendarPlugins: [
        timeGridPlugin,
        interactionPlugin,
        rrulePlugin,
      ],
      eventConstraint: { startTime: '0:00', endTime: '24:00' },
      selectConstraint: { startTime: '0:00', endTime: '24:00' },
      eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      slotLabelFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
      IdOffset: 0,
      localExceptionsAvailabilities: [],
      periodStart: null,
      periodEnd: null,
    };
  },

  computed: {
    formattedHours() {
      const formattedHours = [];
      this.exceptionAvailabilities.filter((el) => el.hours.length > 0).forEach((day) => {
        day.hours.forEach((hour) => {
          // avoid duplicated
          const exist = formattedHours.some((el) => el.date === day.date && el.hour === hour);
          if (!exist) {
            formattedHours.push({
              date: day.date,
              day: day.day,
              hour,
            });
          }
        });
      });
      return formattedHours;
    },

    recurringAvailabilitiesWithExceptions() {
      const list = this.recurringAvailabilities.map((el) => {
        const event = { ...el };

        this.exceptionAvailabilities
          .filter((exc) => exc.day === event.saveDay)
          .forEach((exc) => {
            const [hour, minute] = event.saveTime.split(':');
            const d = this.$moment(exc.date).hour(hour).minute(minute).format('YYYYMMDDTHHmmss');
            event.rrule += `\nEXDATE:${d}`;
          });

        return event;
      });

      return list;
    },

    allAvailabilities() {
      if (this.isLoadingExceptions) {
        return [];
      }
      return [...this.recurringAvailabilitiesWithExceptions, ...this.localExceptionsAvailabilities];
    },
  },

  watch: {
    formattedHours() {
      // console.log('formattedHours');
      let eventStart = null;
      let eventEnd = null;
      this.localExceptionsAvailabilities = [];

      this.formattedHours.forEach((el) => {
        const [hour, minute] = el.hour.split(':');
        if (!eventStart && !eventEnd) {
          // console.log('no event start');
          eventStart = this.$moment(el.date)
            .hour(parseInt(hour, 10))
            .minute(parseInt(minute, 10));
          eventEnd = this.$moment(el.date)
            .hour(parseInt(hour, 10))
            .minute(parseInt(minute, 10))
            .add(30, 'minutes');
        } else {
          const newEventStart = this.$moment(el.date)
            .hour(parseInt(hour, 10))
            .minute(parseInt(minute, 10));
          if (eventEnd.isSame(newEventStart)) {
            eventEnd.add(30, 'minutes');
          } else {
            this.localExceptionsAvailabilities.push({
              start: eventStart.format(),
              end: eventEnd.format(),
              allDay: false,
              editable: true,
              backgroundColor: 'green',
              borderColor: 'green',
              title: '(one time)',
              type: 'single',
              id: this.lastId + this.IdOffset,
            });
            this.IdOffset += 1;

            eventStart = this.$moment(el.date)
              .hour(parseInt(hour, 10))
              .minute(parseInt(minute, 10));
            eventEnd = this.$moment(el.date)
              .hour(parseInt(hour, 10))
              .minute(parseInt(minute, 10))
              .add(30, 'minutes');
          }
        }
      });

      if (eventStart && eventEnd) {
        this.localExceptionsAvailabilities.push({
          start: eventStart.format(),
          end: eventEnd.format(),
          allDay: false,
          editable: true,
          backgroundColor: 'green',
          borderColor: 'green',
          title: '(one time)',
          type: 'single',
          id: this.lastId + this.IdOffset,
        });
        this.IdOffset += 1;
      }
    },
  },

  methods: {
    eventRender(info) {
      // fix a fullcalendar bug where an event has duration of 24 hours,
      // it display only the start time
      const momentStart = this.$moment(info.event.start);
      const momentEnd = this.$moment(info.event.end);

      // make sure event cannot be on 2 days
      if (momentStart.day() !== momentEnd.day()) {
        const newEnd = momentStart.clone().hour(23).minute(59);

        const timeLabel = info.el.querySelector('.fc-time');
        if (timeLabel) {
          timeLabel.innerHTML = `${momentStart.format('HH:mm')} - ${newEnd.format('HH:mm')}`;
        }
      }
    },

    eventAllow(dropLocation, draggedEvent) {
      if (draggedEvent.end < this.$moment().endOf('day').toDate()) return false;
      // don t availabilities event less than 90
      if (this.$moment(draggedEvent.end).diff(this.$moment(draggedEvent.start), 'minutes') < 90
        && this.$moment(draggedEvent.start).hours() < 22) {
        return false;
      }
      return dropLocation.start > this.$moment().endOf('day').toDate();
    },

    addSingleAvailability(start, end) {
      if (start < this.$moment().endOf('day').toDate()) return;
      const exceptions = [...this.exceptionAvailabilities];
      const momentStart = this.$moment(start);
      let momentEnd = this.$moment(end);

      // dont allow event shorter than 90 minutes (except after 22:30)
      if (momentEnd.diff(momentStart, 'minutes') < 90
        && momentStart.isBefore(momentStart.clone().hours(22).minutes(30))) {
        momentEnd = momentStart.clone().add(90, 'minutes');
      }

      let index = exceptions.findIndex((el) => el.date === momentStart.format('YYYY-MM-DD'));
      if (index < 0) {
        const day = momentStart.format('dddd').toLowerCase();
        exceptions.push({
          date: momentStart.format('YYYY-MM-DD'),
          day,
          hours: [],
        });
        index = exceptions.length - 1;

        // add other recurring time for that day as one time
        const recurringToConvert = this.recurringAvailabilitiesWithExceptions.filter((el) => {
          const d = this.$moment(momentStart).format('YYYYMMDDTHHmmss');
          return el.saveDay === day && !el.rrule.includes(`EXDATE:${d}`);
        });

        recurringToConvert.forEach((el) => {
          const [hour, minute] = el.saveTime.split(':');
          const [hourDuration, minuteDuration] = el.duration.split(':');
          const startRecurring = this.$moment(start).hour(hour).minute(minute);
          const endRecurring = this.$moment(start).hour(hour).minute(minute)
            .add(hourDuration, 'hours')
            .add(minuteDuration, 'minutes');

          do {
            exceptions[index].hours.push(startRecurring.format('HH:mm'));
            startRecurring.add(30, 'minutes');
          } while (startRecurring.isBefore(endRecurring));
        });
      }

      do {
        exceptions[index].hours.push(momentStart.format('HH:mm'));
        momentStart.add(30, 'minutes');
      } while (momentStart.isBefore(momentEnd));

      exceptions.forEach((ex) => { ex.hours.sort(); });

      this.$emit('update:canSaveExceptions', true);
      this.$emit('update:exceptionAvailabilities', exceptions);
    },

    handleDateClick(arg) {
      let endDate;
      if (this.$moment(arg.date).isBefore(this.$moment(arg.date).hours(22).minutes(30))) {
        endDate = this.$moment(arg.date).add(90, 'm').toDate();
      } else {
        endDate = this.$moment(arg.date).hours(23).minutes(59).toDate();
      }
      this.addSingleAvailability(arg.date, endDate);
    },

    handleSelect(arg) {
      this.addSingleAvailability(arg.start, arg.end);
    },

    rescheduleEvent(arg) {
      const oldEventStart = arg.oldEvent
        ? this.$moment(arg.oldEvent.start)
        : this.$moment(arg.prevEvent.start);
      const oldEventEnd = arg.oldEvent
        ? this.$moment(arg.oldEvent.end)
        : this.$moment(arg.prevEvent.end);

      if (oldEventStart.toDate() < this.$moment().endOf('day').toDate()) return;

      const newEventStart = this.$moment(arg.event.start);
      let newEventEnd = this.$moment(arg.event.end);

      // don t allow event with duration shorter than 90 minutes
      if (newEventEnd.diff(newEventStart, 'minutes') < 90) {
        newEventEnd = newEventStart.clone().add(90, 'minutes');
      }

      const exceptions = [...this.exceptionAvailabilities];

      // Search if old event is exception date and remove
      let index = exceptions.findIndex(
        (el) => el.date === oldEventStart.format('YYYY-MM-DD'),
      );

      if (index < 0) {
        exceptions.push({
          date: oldEventStart.format('YYYY-MM-DD'),
          day: oldEventStart.format('dddd').toLowerCase(),
          hours: [],
        });
        index = exceptions.length - 1;

        // add other recurring time for that day as one time
        const recurringToConvert = this.recurringAvailabilitiesWithExceptions.filter((el) => {
          const exdate = oldEventStart.format('YYYYMMDDTHHmmss');
          const day = oldEventStart.format('dddd').toLowerCase();
          return el.saveDay === day && !el.rrule.includes(`EXDATE:${exdate}`);
        });

        recurringToConvert.forEach((el) => {
          const [hour, minute] = el.saveTime.split(':');
          const [hourDuration, minuteDuration] = el.duration.split(':');
          const oldStart = arg.oldEvent ? arg.oldEvent.start : arg.prevEvent.start;
          const startRecurring = this.$moment(oldStart).hour(hour).minute(minute);
          const endRecurring = this.$moment(oldStart).hour(hour).minute(minute)
            .add(hourDuration, 'hours')
            .add(minuteDuration, 'minutes');

          do {
            if (startRecurring.isBefore(oldEventStart) || startRecurring.isAfter(oldEventEnd)) {
              exceptions[index].hours.push(startRecurring.format('HH:mm'));
            }
            startRecurring.add(30, 'minutes');
          } while (startRecurring.isBefore(endRecurring));
        });
      } else {
        exceptions[index].hours = exceptions[index].hours.filter((el) => {
          const [hour, minute] = el.split(':');
          const date = this.$moment(exceptions[index].date).hour(hour).minute(minute);
          return date.isBefore(oldEventStart) || date.isAfter(oldEventEnd);
        });
      }

      // Add new event as one time event
      index = exceptions.findIndex((el) => el.date === newEventStart.format('YYYY-MM-DD'));
      if (index < 0) {
        const day = newEventStart.format('dddd').toLowerCase();
        exceptions.push({
          date: newEventStart.format('YYYY-MM-DD'),
          day,
          hours: [],
        });
        index = exceptions.length - 1;

        // add other recurring time for that day as one time
        const recurringToConvert = this.recurringAvailabilitiesWithExceptions.filter((el) => {
          const d = this.$moment(newEventStart).format('YYYYMMDDTHHmmss');
          return el.saveDay === day && !el.rrule.includes(`EXDATE:${d}`);
        });

        recurringToConvert.forEach((el) => {
          const [hour, minute] = el.saveTime.split(':');
          const [hourDuration, minuteDuration] = el.duration.split(':');
          const startRecurring = this.$moment(arg.event.start).hour(hour).minute(minute);
          const endRecurring = this.$moment(arg.event.start).hour(hour).minute(minute)
            .add(hourDuration, 'hours')
            .add(minuteDuration, 'minutes');

          do {
            exceptions[index].hours.push(startRecurring.format('HH:mm'));
            startRecurring.add(30, 'minutes');
          } while (startRecurring.isBefore(endRecurring));
        });
      }

      do {
        exceptions[index].hours.push(newEventStart.format('HH:mm'));
        newEventStart.add(30, 'minutes');
      } while (newEventStart.isBefore(newEventEnd));

      exceptions.forEach((ex) => { ex.hours.sort(); });

      this.$emit('update:canSaveExceptions', true);
      this.$emit('update:exceptionAvailabilities', exceptions);
    },

    removeEvent(arg) {
      const exceptions = [...this.exceptionAvailabilities];
      const start = this.$moment(arg.event.start);
      const end = this.$moment(arg.event.end);

      if (start.toDate() < this.$moment().endOf('day').toDate()) return;

      let index = exceptions.findIndex(
        (el) => el.date === start.format('YYYY-MM-DD'),
      );
      if (index < 0) {
        exceptions.push({
          date: start.format('YYYY-MM-DD'),
          day: start.format('dddd').toLowerCase(),
          hours: [],
        });
        index = exceptions.length - 1;

        // add other recurring time for that day as one time
        const recurringToConvert = this.recurringAvailabilitiesWithExceptions.filter((el) => {
          const exdate = start.format('YYYYMMDDTHHmmss');
          const day = start.format('dddd').toLowerCase();
          return el.saveDay === day && !el.rrule.includes(`EXDATE:${exdate}`);
        });

        recurringToConvert.forEach((el) => {
          const [hour, minute] = el.saveTime.split(':');
          const [hourDuration, minuteDuration] = el.duration.split(':');
          const startRecurring = this.$moment(start).hour(hour).minute(minute);
          const endRecurring = this.$moment(start).hour(hour).minute(minute)
            .add(hourDuration, 'hours')
            .add(minuteDuration, 'minutes');

          do {
            if (startRecurring.isBefore(start) || startRecurring.isAfter(end)) {
              exceptions[index].hours.push(startRecurring.format('HH:mm'));
            }
            startRecurring.add(30, 'minutes');
          } while (startRecurring.isBefore(endRecurring));
        });
        exceptions.forEach((ex) => { ex.hours.sort(); });
        this.$emit('update:canSaveExceptions', true);
        this.$emit('update:exceptionAvailabilities', exceptions);
      } else {
        exceptions[index].hours = exceptions[index].hours.filter((el) => {
          const [hour, minute] = el.split(':');
          const date = this.$moment(exceptions[index].date).hour(hour).minute(minute);
          return date.isBefore(start) || date.isAfter(end);
        });
        exceptions.forEach((ex) => { ex.hours.sort(); });
        this.$emit('update:canSaveExceptions', true);
        this.$emit('update:exceptionAvailabilities', exceptions);
      }
    },

    updatePeriod(arg) {
      const currentStart = this.$moment(arg.view.currentStart).format();
      const currentEnd = this.$moment(arg.view.currentEnd).format();

      if (this.periodStart !== currentStart || this.periodEnd !== currentEnd) {
        this.$emit('update:canSaveExceptions', false);
        this.$emit('update:singleAvailabilityPeriod', {
          start: arg.view.currentStart,
          end: arg.view.currentEnd,
        });

        this.periodStart = currentStart;
        this.periodEnd = currentEnd;
      }
    },
  },
};
</script>

<style lang='scss'>
@import '~@fullcalendar/core/main.css';
@import '~@fullcalendar/timegrid/main.css';

.fullCalendarSingleAvailability {
  // My availability - One-time

  .fc-toolbar {
    font-size: 18px;

    h2 {
      font-size: 1em;
    }

    .fc-button {
      background-color: transparent;
      border-color: transparent;
      box-shadow: none;
    }

    &.fc-header-toolbar {
      margin-bottom: 0;

      .fc-today-button {
        text-transform: capitalize;
      }
    }
  }

  .fc {
    .fc-view-container {
      background-color: white;
      color: black;

      .fc-head {
        .fc-day-header {
          font-weight: normal;
          font-size: 12px;
          height: 25px;
          padding: 2px 0;
          vertical-align: middle;
        }
      }
    }

    .fc-scroller.fc-time-grid-container {
      min-height: 525px;
    }

    .fc-content {
      position: sticky;
      top: 0;

      .fc-time {
        font-size: 12px;
        text-align: center;

        span {
          display: inline-block;
          word-break: break-word;
          white-space: normal;
        }
      }
    }
  }

  &.tabletMode {
    .fc {
      .fc-view-container {
        overflow: auto;
      }

      .fc-view.fc-timeGridWeek-view.fc-timeGrid-view {
        width: 200%;
      }

      .fc-left {
        h2 {
          font-size: 1.25rem;
        }
      }
    }
  }

  &.mobileMode {
    .fc {
      .fc-view-container {
        table {
          font-size: 0.85em;
        }
      }

      .fc-left {
        width: 40%;

        h2 {
          font-size: 1rem;
        }
      }

      .fc-right {
        width: 60%;

        .fc-today-button {
          width: 44%;
        }

        .fc-button-group {
          width: 55%;
          margin-left: 1%;
        }
      }

      .fc-scroller.fc-time-grid-container {
        min-height: 360px;
      }
    }
  }
}

</style>
